import React from 'react';
import clsx from 'clsx';
import {SetOptional} from 'type-fest';
import {type AutoListCommonExtendProps} from '@netx/core-components/AutoListField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {
	GroupAutoList,
	UserAutoList,
	UserGroupAutoList,
	type IUserAutoListItem,
	type IUserGroupAutoListItem,
} from '@netx/core-user-next';
import {UserGroupItemType} from '../../constants';
import {USER_SELECTOR_LABEL} from '../../locales';
import type {EnhancedUserSelector} from '../../types';
import {UserSelectorListItems} from './UserSelectorListItems';
import {
	userSelectorClasses,
	getUserSelectorUtilityClass,
	UserSelectorClasses,
	USER_SELECTOR_CLASS_NAME,
} from './userSelectorClasses';

export type UserSelectorOwnerState = WithClasses<UserSelectorClasses, {}>;

const useUtilityClasses = (ownerState: UserSelectorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getUserSelectorUtilityClass, classes);
};

const UserSelectorRoot = styled('div', {
	name: USER_SELECTOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({}));

const UserSelectorHeader = styled('div', {
	name: USER_SELECTOR_CLASS_NAME,
	slot: 'Header',
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	marginBottom: theme.spacing(2),
}));

export type UserSelectorProps = WithClasses<
	UserSelectorClasses,
	{
		disabled?: boolean;
		disableGroupSelection?: boolean;
		disableNone?: boolean;
		disableUserSelection?: boolean;
		onChange(userSelector: PartialUserSelector): void;
		userSelector: PartialUserSelector;
	}
>;

type PartialUserSelector = SetOptional<EnhancedUserSelector, 'userSelectorId'>;

type UserGroupAutoLVB = {
	label: string;
	value: number;
};

const userAutoListItemToUserGroupItem = (
	userAutoListItem: IUserAutoListItem,
	type: UserGroupItemType,
): IUserGroupAutoListItem => ({
	...userAutoListItem,
	type,
});

const lbvToUserGroupItem = (
	lvb: UserGroupAutoLVB,
	type: UserGroupItemType,
): IUserGroupAutoListItem => ({
	name: lvb.label,
	id: lvb.value,
	email: '',
	type,
});

const getGroupIdsFromUserSelector = (userSelector: PartialUserSelector) =>
	userSelector.groups.map(({groupId}) => groupId);

const getUserIdsFromUserSelector = (userSelector: PartialUserSelector) =>
	userSelector.users.map(({userId}) => userId);

const addToSelector = (
	userGroupItem: IUserGroupAutoListItem,
	userSelector: PartialUserSelector,
): PartialUserSelector => {
	const allWereSelected = userSelector.all;

	const groups =
		userGroupItem.type === UserGroupItemType.GROUP
			? allWereSelected
				? [
						{
							groupId: userGroupItem.id,
							groupLabel: userGroupItem.name,
						},
					]
				: userSelector.groups.concat([
						{
							groupId: userGroupItem.id,
							groupLabel: userGroupItem.name,
						},
					])
			: userSelector.groups;

	const users =
		userGroupItem.type === UserGroupItemType.USER
			? allWereSelected
				? [
						{
							userId: userGroupItem.id,
							userLabel: userGroupItem.name,
						},
					]
				: userSelector.users.concat([
						{
							userId: userGroupItem.id,
							userLabel: userGroupItem.name,
						},
					])
			: userSelector.users;

	return {
		...userSelector,
		all: !groups.length && !users.length,
		none: false,
		users,
		groups,
	};
};

const removeFromSelector = (
	userGroupItem: IUserGroupAutoListItem,
	userSelector: PartialUserSelector,
): PartialUserSelector => {
	if (userGroupItem.id === -1) {
		return {
			...userSelector,
			all: false,
			none: !userSelector.groups.length && !userSelector.users.length,
		};
	}

	const groups =
		userGroupItem.type === UserGroupItemType.GROUP
			? userSelector.groups.filter(
					(group) => group.groupId !== userGroupItem.id,
				)
			: userSelector.groups;

	const users =
		userGroupItem.type === UserGroupItemType.USER
			? userSelector.users.filter((user) => user.userId !== userGroupItem.id)
			: userSelector.users;

	return {
		...userSelector,
		all: !groups.length && !users.length,
		none: false,
		users,
		groups,
	};
};

export const UserSelectorPrimitive = ({
	classes: _classes,
	className,
	disabled,
	disableGroupSelection,
	disableNone,
	disableUserSelection,
	onChange,
	userSelector,
}: UserSelectorProps) => {
	const ownerState: UserSelectorOwnerState = {
		classes: _classes,
		className,
	};
	const classes = useUtilityClasses(ownerState);

	const commonAutoListProps: Partial<
		AutoListCommonExtendProps<any, false, true> & {
			inputValue: string;
			notForValueDisplay: boolean;
		}
	> = {
		notForValueDisplay: true,
		disableClearable: true,
		disabled,
		hideLabel: true,
		multiple: false,
		variant: 'filled',
		InputProps: {
			InputLabelProps: {
				shrink: true,
			},
		},
	} as const;

	return (
		<UserSelectorRoot className={clsx(classes.root, className)}>
			<UserSelectorHeader>
				{!disableGroupSelection && !disableUserSelection ? (
					<UserGroupAutoList<false, true>
						placeholder={USER_SELECTOR_LABEL.SELECT_GROUPS_USERS}
						{...commonAutoListProps}
						filterOptions={(usersOrGroups) => {
							if (userSelector.all) return usersOrGroups;

							const groupIds = getGroupIdsFromUserSelector(userSelector);
							const userIds = getUserIdsFromUserSelector(userSelector);
							const results = usersOrGroups.filter(({type, id}) => {
								if (type === UserGroupItemType.GROUP)
									return !groupIds.includes(id);
								if (type === UserGroupItemType.USER)
									return !userIds.includes(id);
							});

							if (results[0]?.id !== -1)
								results.unshift({
									name: USER_SELECTOR_LABEL.ALL,
									email: '',
									id: -1,
									type: UserGroupItemType.USER,
								});

							return results;
						}}
						onChange={(event, value) => {
							if (!value) return;
							if (value.id === -1) {
								onChange({
									...userSelector,
									all: true,
									none: false,
								});
								return;
							}
							onChange(addToSelector(value, userSelector));
						}}
					/>
				) : !disableGroupSelection ? (
					<GroupAutoList
						placeholder={USER_SELECTOR_LABEL.SELECT_GROUPS}
						{...commonAutoListProps}
						filterOptions={(groups) => {
							if (userSelector.all) return [...groups];
							const groupIds = getGroupIdsFromUserSelector(userSelector);
							const results = groups.filter(
								({value: id}) => !groupIds.includes(id),
							);
							if (results[0]?.value !== -1)
								results.unshift({
									label: USER_SELECTOR_LABEL.ALL,
									value: -1,
								});
							return results;
						}}
						onChange={(event, value) => {
							if (!value) return;
							if (value.value === -1) {
								onChange({
									...userSelector,
									all: true,
									none: false,
								});
								return;
							}
							onChange(
								addToSelector(
									lbvToUserGroupItem(value, UserGroupItemType.GROUP),
									userSelector,
								),
							);
						}}
					/>
				) : !disableUserSelection ? (
					<UserAutoList
						placeholder={USER_SELECTOR_LABEL.SELECT_USERS}
						{...commonAutoListProps}
						filterOptions={(users) => {
							if (userSelector.all) return users;
							const userIds = getUserIdsFromUserSelector(userSelector);
							const results = users.filter(({id}) => !userIds.includes(id));
							if (results[0]?.id !== -1)
								results.unshift({
									name: USER_SELECTOR_LABEL.ALL,
									email: '',
									id: -1,
								});
							return results;
						}}
						onChange={(event, value) => {
							if (!value) return;
							if (value.id === -1) {
								onChange({
									...userSelector,
									all: true,
									none: false,
								});
								return;
							}
							onChange(
								addToSelector(
									userAutoListItemToUserGroupItem(
										value,
										UserGroupItemType.USER,
									),
									userSelector,
								),
							);
						}}
					/>
				) : null}
			</UserSelectorHeader>

			<UserSelectorListItems
				hideSubtitle={
					(userSelector.all && !disableNone) ||
					disableUserSelection ||
					disableGroupSelection
				}
				disableNone={disableNone}
				onRemove={(userGroupItem) => {
					onChange(removeFromSelector(userGroupItem, userSelector));
				}}
				items={
					userSelector.all
						? [
								lbvToUserGroupItem(
									{label: USER_SELECTOR_LABEL.ALL, value: -1},
									UserGroupItemType.USER,
								),
							]
						: userSelector.groups
								.map<IUserGroupAutoListItem>(
									({groupId: value, groupLabel: label}) =>
										lbvToUserGroupItem({label, value}, UserGroupItemType.GROUP),
								)
								.concat(
									userSelector.users.map<IUserGroupAutoListItem>(
										({userId: value, userLabel: label}) =>
											lbvToUserGroupItem(
												{label, value},
												UserGroupItemType.USER,
											),
									),
								)
				}
			/>
		</UserSelectorRoot>
	);
};

export const UserSelector = UserSelectorPrimitive;

export default UserSelector;
